import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/common/DataTable';
import { usePagedData } from '../../hooks/usePagedData';
import { syncApi } from '../../api/endpoints';
import Select from '../../components/common/Select';
import { SkeletonTable } from '../../components/common/Skeleton';
import {
  FiSearch, FiRefreshCw, FiCheckCircle, FiXCircle, FiClock, FiLoader, FiActivity,
  FiChevronDown, FiChevronRight,
} from 'react-icons/fi';
import { toast } from 'react-toastify';

interface SyncLogItem {
  id: string;
  entityType: string;
  entityId: string | null;
  channel: string;
  operation: string;
  status: string;
  errorMessage: string | null;
  durationMs: number;
  createdAt: string;
}

const ENTITY_TYPES = ['PurchaseOrder', 'Product', 'Invoice', 'Inventory', 'Shipment'];

const SyncLogsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const {
    data,
    loading,
    query,
    filters,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    refresh,
  } = usePagedData<SyncLogItem>(syncApi.logs, {
    initialSortBy: 'createdAt',
    initialSortDesc: true,
    initialPageSize: 50,
  });

  // Fix #1: Wrap setFilters to also reset page to 1 when filters change
  const handleFilterChange = useCallback((updater: (prev: any) => any) => {
    setFilters(updater);
    setPage(1);
  }, [setFilters, setPage]);

  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  }, [searchInput, setSearch]);

  const handleSyncAll = useCallback(async () => {
    setSyncing(true);
    try {
      await syncApi.syncAll();
      toast.success(t('sync.syncAllStarted'));
      setTimeout(() => refresh(), 2000);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('sync.syncError'));
    } finally {
      setSyncing(false);
    }
  }, [refresh, t]);

  // Fix #4: Toggle expanded row (supports multiple rows expanded at once)
  const toggleExpandedRow = useCallback((id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const formatDate = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString('pl-PL') + ' ' + date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  };

  // Fix #3: Stats calculated from current page data
  const items = data?.items || [];
  const totalOnPage = items.length;
  const totalCompleted = items.filter(i => i.status === 'Completed').length;
  const totalFailed = items.filter(i => i.status === 'Failed').length;
  const avgDuration = items.length > 0
    ? Math.round(items.reduce((s, i) => s + i.durationMs, 0) / items.length)
    : 0;

  const columns = useMemo(() => [
    {
      key: 'createdAt',
      header: t('sync.date'),
      sortable: true,
      render: (row: SyncLogItem) => (
        <span style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(row.createdAt)}</span>
      ),
    },
    {
      key: 'entityType',
      header: t('sync.type'),
      render: (row: SyncLogItem) => (
        <span className="badge badge-active" style={{ fontSize: '10px' }}>{row.entityType}</span>
      ),
    },
    {
      key: 'operation',
      header: t('sync.operation'),
      render: (row: SyncLogItem) => (
        <span style={{ fontSize: '12px', fontWeight: 500 }}>{row.operation}</span>
      ),
    },
    {
      key: 'channel',
      header: t('sync.channel'),
      render: (row: SyncLogItem) => (
        <span style={{ fontSize: '11px' }}>{row.channel === 'Vendor' ? '🏢 Vendor' : '🛒 Seller'}</span>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      sortable: true,
      render: (row: SyncLogItem) => {
        const STATUS_MAP: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
          Pending: { label: t('sync.statusPending'), color: '#94a3b8', icon: <FiClock /> },
          InProgress: { label: t('sync.statusInProgress'), color: '#f59e0b', icon: <FiLoader className="spin" /> },
          Completed: { label: t('sync.statusCompleted'), color: '#10b981', icon: <FiCheckCircle /> },
          Failed: { label: t('sync.statusFailed'), color: '#ef4444', icon: <FiXCircle /> },
        };
        const s = STATUS_MAP[row.status] || { label: row.status, color: '#94a3b8', icon: <FiClock /> };
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: s.color, fontWeight: 600, fontSize: '12px' }}>
            {s.icon} {s.label}
          </span>
        );
      },
    },
    {
      key: 'durationMs',
      header: t('sync.duration'),
      sortable: true,
      render: (row: SyncLogItem) => (
        <span style={{
          fontSize: '11px',
          fontFamily: 'monospace',
          color: row.durationMs > 5000 ? '#ef4444' : row.durationMs > 2000 ? '#f59e0b' : 'var(--text-secondary)',
        }}>
          {formatDuration(row.durationMs)}
        </span>
      ),
    },
    {
      key: 'errorMessage',
      header: t('sync.details'),
      render: (row: SyncLogItem) => {
        if (row.status === 'Failed' && row.errorMessage) {
          const isExpanded = expandedRows.has(row.id);
          return (
            <span
              style={{ fontSize: '11px', color: '#ef4444', cursor: 'pointer', maxWidth: '200px', display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={row.errorMessage}
              onClick={(e) => { e.stopPropagation(); toggleExpandedRow(row.id); }}
            >
              {isExpanded ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
              {row.errorMessage}
            </span>
          );
        }
        if (row.entityId) {
          return <span style={{ fontSize: '10px', fontFamily: 'monospace', color: 'var(--text-muted)' }}>{row.entityId.substring(0, 8)}...</span>;
        }
        return <span style={{ color: 'var(--text-muted)' }}>—</span>;
      },
    },
  ], [t, expandedRows, toggleExpandedRow]);

  // Fix #4: Render expanded row content inline
  const renderExpandedRow = useCallback((row: SyncLogItem) => {
    if (!row.errorMessage) return null;
    return (
      <div style={{
        padding: '12px 16px',
        background: 'var(--danger-light, #fef2f2)',
        borderLeft: '3px solid var(--danger, #ef4444)',
      }}>
        <div style={{ fontWeight: 700, color: 'var(--danger, #ef4444)', marginBottom: '4px', fontSize: '12px' }}>
          {t('sync.errorDetails')} — {row.operation} ({row.entityType})
        </div>
        <pre style={{
          margin: 0,
          fontSize: '11px',
          whiteSpace: 'pre-wrap',
          color: 'var(--danger, #ef4444)',
          fontFamily: 'monospace',
          maxHeight: '150px',
          overflow: 'auto',
        }}>
          {row.errorMessage}
        </pre>
      </div>
    );
  }, [t]);

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <h1>{t('sync.title')}</h1>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleSyncAll} disabled={syncing}>
            <FiRefreshCw className={syncing ? 'spin' : ''} />
            {syncing ? t('sync.syncing') : t('sync.syncAll')}
          </button>
        </div>
      </div>

      {/* Fix #3: Mini stats calculated from current page data */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
        <div className="card">
          <div className="card-body" style={{ padding: '12px', textAlign: 'center' }}>
            <FiActivity style={{ color: '#6366f1', fontSize: '18px' }} />
            <div style={{ fontSize: '20px', fontWeight: 800 }}>{totalOnPage}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{t('common.total')}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ padding: '12px', textAlign: 'center' }}>
            <FiCheckCircle style={{ color: '#10b981', fontSize: '18px' }} />
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#10b981' }}>{totalCompleted}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{t('sync.successPage')}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ padding: '12px', textAlign: 'center' }}>
            <FiXCircle style={{ color: '#ef4444', fontSize: '18px' }} />
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#ef4444' }}>{totalFailed}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{t('sync.errorsPage')}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ padding: '12px', textAlign: 'center' }}>
            <FiClock style={{ color: '#f59e0b', fontSize: '18px' }} />
            <div style={{ fontSize: '20px', fontWeight: 800 }}>{formatDuration(avgDuration)}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{t('sync.avgTime')}</div>
          </div>
        </div>
      </div>

      <div className="toolbar">
        <form className="toolbar-search" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder={t('sync.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary"><FiSearch /></button>
          </div>
        </form>
        <div className="toolbar-filters" style={{ display: 'flex', gap: '0.5rem' }}>
          {/* Fix #1 & #2: Filters now reset page and include InProgress */}
          <Select
            options={[
              { value: '', label: t('common.allStatuses') },
              { value: 'Completed', label: t('sync.statusCompleted') },
              { value: 'Failed', label: t('sync.statusFailed') },
              { value: 'Pending', label: t('sync.statusPending') },
              { value: 'InProgress', label: t('sync.statusInProgress') },
            ]}
            value={String(filters.status ?? '')}
            onChange={(v) => handleFilterChange((p: any) => ({ ...p, status: v || undefined }))}
            placeholder={t('common.allStatuses')}
          />
          <Select
            options={[
              { value: '', label: t('common.allTypes') },
              ...ENTITY_TYPES.map(et => ({ value: et, label: et })),
            ]}
            value={String(filters.entityType ?? '')}
            onChange={(v) => handleFilterChange((p: any) => ({ ...p, entityType: v || undefined }))}
            placeholder={t('common.allTypes')}
          />
        </div>
      </div>

      {loading ? (
        <SkeletonTable rows={12} cols={7} />
      ) : (
        <DataTable
          columns={columns}
          data={items}
          totalCount={data?.totalCount || 0}
          page={query.page}
          pageSize={query.pageSize}
          totalPages={data?.totalPages || 0}
          sortBy={query.sortBy}
          sortDescending={query.sortDescending}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={setSort}
          expandedRowIds={expandedRows}
          renderExpandedRow={renderExpandedRow}
        />
      )}
    </div>
  );
};

export default SyncLogsPage;
